/**
 * Forest ambience + UI sounds, synthesized locally with the Web Audio API —
 * no external audio files, no network requests.
 */

let context: AudioContext | null = null;

function ctx(): AudioContext {
  if (!context) context = new (window.AudioContext || (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  if (context.state === "suspended") void context.resume();
  return context;
}

/* ---------- Forest ambience ---------- */

interface Ambience {
  stop: () => void;
}

let currentAmbience: Ambience | null = null;

/** Layered forest white-noise: wind through leaves, distant rumble, birds. */
export type AmbienceKind = "forest" | "rain" | "waves" | "wind";

export function startForestAmbience(kind: AmbienceKind = "forest"): Ambience {
  if (currentAmbience) currentAmbience.stop();
  const audio = ctx();
  const master = audio.createGain();
  master.gain.value = 0;
  master.connect(audio.destination);
  master.gain.linearRampToValueAtTime(0.5, audio.currentTime + 2.5);

  const stoppable: Array<{ stop: () => void }> = [];

  // Shared pink-ish noise bed for every ambience
  const noiseBuffer = audio.createBuffer(1, audio.sampleRate * 4, audio.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < data.length; i += 1) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.2;
  }
  const makeNoise = (filterType: BiquadFilterType, frequency: number, q: number, gainValue: number) => {
    const source = audio.createBufferSource();
    source.buffer = noiseBuffer;
    source.loop = true;
    const filter = audio.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = q;
    const gain = audio.createGain();
    gain.gain.value = gainValue;
    source.connect(filter).connect(gain).connect(master);
    source.start();
    stoppable.push({ stop: () => { try { source.stop(); } catch { /* already stopped */ } } });
    return { gain, filter };
  };

  if (kind === "forest") {
    makeNoise("lowpass", 480, 0.7, 0.55);
    const leaves = makeNoise("bandpass", 2600, 0.6, 0.05);
    const wander = audio.createOscillator();
    const wanderAmount = audio.createGain();
    wander.frequency.value = 0.07;
    wanderAmount.gain.value = 0.035;
    wander.connect(wanderAmount).connect(leaves.gain.gain);
    wander.start();
    stoppable.push({ stop: () => { try { wander.stop(); } catch { /* already stopped */ } } });
  } else if (kind === "rain") {
    makeNoise("highpass", 1400, 0.4, 0.16);
    makeNoise("bandpass", 5200, 0.5, 0.1);
    makeNoise("lowpass", 220, 0.8, 0.35);
  } else if (kind === "waves") {
    const surf = makeNoise("lowpass", 900, 0.5, 0.0001);
    surf.gain.gain.value = 0.1;
    const swell = audio.createOscillator();
    const swellAmount = audio.createGain();
    swell.frequency.value = 0.12;
    swellAmount.gain.value = 0.16;
    swell.connect(swellAmount).connect(surf.gain.gain);
    swell.start();
    stoppable.push({ stop: () => { try { swell.stop(); } catch { /* already stopped */ } } });
  } else {
    const wind = makeNoise("lowpass", 640, 0.7, 0.32);
    const gust = audio.createOscillator();
    const gustAmount = audio.createGain();
    gust.frequency.value = 0.05;
    gustAmount.gain.value = 0.18;
    gust.connect(gustAmount).connect(wind.gain.gain);
    gust.start();
    stoppable.push({ stop: () => { try { gust.stop(); } catch { /* already stopped */ } } });
  }

  if (kind === "forest") {
    const chirp = () => {
      if (Math.random() < 0.65) {
        const start = audio.currentTime;
        const base = 2100 + Math.random() * 1400;
        for (let note = 0; note < 2 + Math.floor(Math.random() * 2); note += 1) {
          const osc = audio.createOscillator();
          const gain = audio.createGain();
          const at = start + note * 0.14;
          osc.type = "sine";
          osc.frequency.setValueAtTime(base + Math.random() * 300, at);
          osc.frequency.exponentialRampToValueAtTime(base * 0.72, at + 0.11);
          gain.gain.setValueAtTime(0, at);
          gain.gain.linearRampToValueAtTime(0.05 + Math.random() * 0.04, at + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.13);
          osc.connect(gain).connect(master);
          osc.start(at);
          osc.stop(at + 0.16);
        }
      }
    };
    const birdTimer = window.setInterval(() => {
      if (Math.random() < 0.5) chirp();
    }, 3800);
    window.setTimeout(chirp, 1800);
    stoppable.push({ stop: () => window.clearInterval(birdTimer) });
  }

  const ambience: Ambience = {
    stop: () => {
      master.gain.cancelScheduledValues(audio.currentTime);
      master.gain.setValueAtTime(master.gain.value, audio.currentTime);
      master.gain.linearRampToValueAtTime(0, audio.currentTime + 1.2);
      window.setTimeout(() => stoppable.forEach((node) => node.stop()), 1400);
    }
  };
  currentAmbience = ambience;
  return ambience;
}

export function stopForestAmbience() {
  currentAmbience?.stop();
  currentAmbience = null;
}

export function isAmbiencePlaying() {
  return currentAmbience !== null;
}

/* ---------- One-shot UI sounds ---------- */

/** Warm "pop" when the focus timer finishes — a friendly bubbly ding. */
export function playTimerDoneSound() {
  const audio = ctx();
  const now = audio.currentTime;
  const master = audio.createGain();
  master.gain.value = 0.9;
  master.connect(audio.destination);

  // Bubble pop: fast pitch-down sine blip
  const pop = audio.createOscillator();
  const popGain = audio.createGain();
  pop.type = "sine";
  pop.frequency.setValueAtTime(880, now);
  pop.frequency.exponentialRampToValueAtTime(320, now + 0.09);
  popGain.gain.setValueAtTime(0.001, now);
  popGain.gain.exponentialRampToValueAtTime(0.5, now + 0.015);
  popGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
  pop.connect(popGain).connect(master);
  pop.start(now);
  pop.stop(now + 0.16);

  // Soft follow-up chime
  const chime = audio.createOscillator();
  const chimeGain = audio.createGain();
  chime.type = "triangle";
  chime.frequency.setValueAtTime(1046, now + 0.16);
  chime.frequency.setValueAtTime(1318, now + 0.3);
  chimeGain.gain.setValueAtTime(0.0001, now + 0.16);
  chimeGain.gain.exponentialRampToValueAtTime(0.28, now + 0.2);
  chimeGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.65);
  chime.connect(chimeGain).connect(master);
  chime.start(now + 0.16);
  chime.stop(now + 0.7);
}

/** Small tick for starting a session. */
export function playStartSound() {
  const audio = ctx();
  const now = audio.currentTime;
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(523, now);
  osc.frequency.setValueAtTime(659, now + 0.09);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
  osc.connect(gain).connect(audio.destination);
  osc.start(now);
  osc.stop(now + 0.3);
}
